import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITaxonomySector, ITaxonomyType } from '../interfaces/heavyDutyMachinery/ITaxonomy';
import { getTaxonomy, findTaxonomyTypeByCode } from '../services/TaxonomyService';

/**
 * Fetches the Sector/MachineryFamily/MachineryType taxonomy for the current
 * i18next language (see TaxonomyService's own per-language cache -- many
 * components calling this hook on the same page with the same language
 * still only trigger one network request) and resolves a Machinery.Category/
 * QualifiedCategories/CompatibleCategories value (MachineryType.Code) to its
 * already-localized display name. Re-fetches automatically if the app
 * language changes mid-session. `sectors` is null while loading;
 * `resolveName` falls back to the raw code for one that doesn't match any
 * seeded MachineryType (an orphaned pre-taxonomy value, or a fetch that
 * hasn't resolved yet).
 */
export const useTaxonomy = () => {
  const { i18n } = useTranslation();
  const [sectors, setSectors] = useState<ITaxonomySector[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSectors(null);
    getTaxonomy(i18n.language)
      .then((data) => {
        if (!cancelled) setSectors(data);
      })
      .catch(() => {
        if (!cancelled) setSectors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const resolveName = (code?: string | null): string => {
    if (!code) return '';
    const type: ITaxonomyType | undefined = sectors ? findTaxonomyTypeByCode(sectors, code) : undefined;
    return type ? type.name : code;
  };

  /**
   * Whether a category code is light equipment -- see
   * ITaxonomyType.isLightEquipment. False while the taxonomy is still
   * loading and for a code that matches nothing, which is the safe way
   * round: the wizard then shows every field rather than quietly dropping
   * the ones an excavator needs.
   */
  const isLightEquipment = (code?: string | null): boolean => {
    if (!code || !sectors) return false;
    return findTaxonomyTypeByCode(sectors, code)?.isLightEquipment ?? false;
  };

  return { sectors, loading: sectors === null, resolveName, isLightEquipment };
};
